import * as React from 'react'

import { useEventCallback } from 'usehooks-ts'

export type UseControllableStateParams<T> = {
	value?: T | undefined
	defaultValue?: T | undefined
	onChange?: (state: T) => void
}

type SetStateFn<T> = (prevState?: T) => T

const useUncontrolledState = <T,>({
	defaultValue,
	onChange
}: Omit<UseControllableStateParams<T>, 'prop'>) => {
	const uncontrolledState = React.useState<T | undefined>(defaultValue)
	const [value] = uncontrolledState
	const prevValueRef = React.useRef(value)
	const handleChange = useEventCallback(onChange)

	React.useEffect(() => {
		if (prevValueRef.current !== value) {
			handleChange?.(value as T)
			prevValueRef.current = value
		}
	}, [value, prevValueRef, handleChange])

	return uncontrolledState
}

const useControllableState = <T,>({
	value: prop,
	defaultValue,
	onChange = () => {
		//
	}
}: UseControllableStateParams<T>) => {
	const [uncontrolledProp, setUncontrolledProp] = useUncontrolledState({
		defaultValue,
		onChange
	})
	const isControlled = prop !== undefined
	const value = isControlled ? prop : uncontrolledProp
	const handleChange = useEventCallback(onChange)

	const setValue: React.Dispatch<React.SetStateAction<T | undefined>> =
		React.useCallback(
			nextValue => {
				if (isControlled) {
					const setter = nextValue as SetStateFn<T>
					const value =
						typeof nextValue === 'function' ? setter(prop) : nextValue
					if (value !== prop) handleChange(value as T)
				} else {
					setUncontrolledProp(nextValue)
				}
			},
			[isControlled, prop, setUncontrolledProp, handleChange]
		)

	return [value, setValue] as const
}

export { useControllableState }
